import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../../../application/hooks/useAccount';
import { useUpdateAccount } from '../../../../application/hooks/useUpdateAccount';
import { Account } from '../../../../domain/models/Account';
import { AccountEditFormData } from '../accountEditSchema';

export interface UseAccountEditResult {
  account: Account | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: (data: AccountEditFormData) => Promise<void>;
  onCancel: () => void;
}

/**
 * Hook to manage the account edit form state.
 * Loads current account data and provides submit/cancel handlers.
 */
export function useAccountEdit(accountId: string | undefined): UseAccountEditResult {
  const navigate = useNavigate();
  const { data: account = null, isLoading: loading, error: queryError } = useAccount(accountId);
  const updateAccount = useUpdateAccount(accountId);

  const error = !accountId
    ? 'Account ID is missing'
    : updateAccount.error instanceof Error
      ? updateAccount.error.message
      : updateAccount.error
        ? String(updateAccount.error)
        : queryError instanceof Error
          ? queryError.message
          : queryError
            ? String(queryError)
            : null;

  const onSubmit = async (data: AccountEditFormData) => {
    if (!accountId) return;
    await updateAccount.mutateAsync(
      { name: data.name, timezone: data.timezone },
      { onSuccess: () => navigate(`/accounts/${accountId}`) },
    );
  };

  const onCancel = () => {
    navigate(`/accounts/${accountId}`);
  };

  return { account, loading, submitting: updateAccount.isPending, error, onSubmit, onCancel };
}
